import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import I18n from "i18next";
import { useFormik } from "formik";
import * as yup from "yup";
import MzAlert from "@markaaz/ui/MzAlert";
import MzInput from "@markaaz/ui/MzInputControls";
import MzButton from "@markaaz/ui/MzButton";

const sx = {
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    h1: {
      color: "darkBlue.c100",
      fontSize: "24px",
      fontWeight: "600",
      lineHeight: "36px"
    }
  },

  content: {
    display: "flex",
    flexDirection: "column",
    width: "325px",
    alignItems: "center",
    justifyContent: "center"
  },

  input: {
    margin: "10px 0"
  },

  title: {
    padding: "40px",
    width: "590px",
    textAlign: "center"
  },

  button: {
    margin: "30px 0px 16px 0px"
  }
};

const validationSchema = yup.object({
  code: yup.string().required("Code is required"),
  password: yup
    .string()
    .required("Password is required")
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/, I18n.t("authPages.passwordCriteria")),
  confirmPassword: yup
    .string()
    .required("Please re-enter your password")
    .oneOf([yup.ref("password"), null], "Passwords must match")
});

export default function CreatePassword({ showNotification, onResend, onSubmit, onAlertClose, onSignIn }) {
  const initialValues = {
    code: "",
    password: "",
    confirmPassword: ""
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async () => {
      await onSubmit(formik.values);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true
  });

  return (
    <Box sx={sx.root}>
      <Typography variant="h1" sx={sx.title}>
        {I18n.t("authPages.createNewPassword")}
      </Typography>
      <Box sx={sx.content} component="form">
        <MzInput
          sx={sx.input}
          name="code"
          label="Verification Code"
          autoComplete="off"
          noBorder
          fullWidth
          required
          error={formik.touched.code && !!formik.errors.code}
          helperText={formik.touched.code && formik.errors.code}
          onChange={(event) => {
            const trimmedValue = event.target.value.trim();
            formik.handleChange({
              target: {
                name: "code",
                value: trimmedValue
              }
            });
          }}
          onBlur={formik.handleBlur}
        />

        <MzInput
          sx={sx.input}
          name="password"
          label="New Password"
          autoComplete="new-password"
          type="password"
          noBorder
          required
          fullWidth
          error={formik.touched.password && !!formik.errors.password}
          helperText={formik.touched.password && formik.errors.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        ></MzInput>
        <MzInput
          sx={sx.input}
          name="confirmPassword"
          label="Confirm New Password"
          autoComplete="new-password"
          type="password"
          noBorder
          fullWidth
          required
          error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        ></MzInput>
        <MzButton
          variant="contained"
          color="primary"
          sx={sx.button}
          size="large"
          disabled={!formik.isValid || formik.isSubmitting}
          loading={formik.isSubmitting}
          onClick={formik.handleSubmit}
        >
          {I18n.t("common.submit")}
        </MzButton>
        <MzButton onClick={onResend}>{I18n.t("authPages.resendCode")}</MzButton>
        <MzButton onClick={onSignIn}>Back to Sign In</MzButton>
      </Box>
      <Box mt={5}>
        {showNotification.message && (
          <MzAlert
            sx={{ alignItems: "center", width: "450px" }}
            severity={showNotification.type === "success" ? "success" : "error"}
            onClose={onAlertClose}
          >
            {showNotification.message}
          </MzAlert>
        )}
      </Box>
    </Box>
  );
}
